import React from 'react';

import {
  CustomAccordion,
  CustomAccordionItem,
  CustomAccordionItemHeading,
  CustomAccordionItemButton,
  CustomAccordionItemPanel,
} from '../../components/UI/CustomAccordion/CustomAccordion';
import ServicesList from '../ServicesList/ServicesList';
import Button, { themes, sizes } from '../UI/Button/Button';
import Content from '../UI/Content/Content';
import CqcRating from '../UI/CqcRating/CqcRating';
import { Gallery } from '../UI/Gallery/Gallery';
import HtmlContent from '../UI/HtmlContent/HtmlContent';
import Icon from '../UI/Icon/Icon';
import { SocialLinks } from '../UI/Social/Social';
import Type from '../UI/Type/Type';
import * as styles from './LocationDetails.module.scss';

const LocationDetails = ({
  id,
  body,
  address,
  telephone,
  fax,
  email,
  openingTimes,
  mapLocation,
  gallery,
  twitter,
  facebook,
  youtube,
  services,
  directions,
  showGallery = false,
  showServices = false,
  showDirections = false,
  directionsLink,
  googleDirections,
  cqcId,
  displayRating,
}) => {
  const socialLinks = [
    facebook && { url: facebook, label: 'facebook' },
    twitter && { url: twitter, label: 'twitter' },
    youtube && { url: youtube, label: 'youtube' },
  ].filter(Boolean);
  const hasDirections = showDirections && directions?.length;
  const directionsId = directionsLink || `getting-here-${id}`;

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {showGallery && <Gallery images={gallery} />}

        <Content items={body} hasLead={false} />
      </div>

      <div className={styles.metaCol1}>
        <Type.MetaHeading>Contact details</Type.MetaHeading>

        <address>
          <p>{address}</p>

          {(hasDirections || googleDirections) && (
            <Button
              theme={themes.LINK}
              to={`#${directionsId}` || googleDirections}
            >
              How to get here
            </Button>
          )}

          {(telephone || email) && (
            <ul className={styles.contactMethods}>
              {telephone && (
                <li>
                  <Icon name="phone" />{' '}
                  <a href={`tel:${telephone.replace(/ /g, '')}`}>{telephone}</a>
                </li>
              )}

              {fax && (
                <li>
                  <Icon name="printer" /> {fax}
                </li>
              )}

              {email && (
                <li>
                  <Icon name="mail" /> <a href={`mailto:${email}`}>{email}</a>
                </li>
              )}
            </ul>
          )}

          <SocialLinks items={socialLinks} />
        </address>
      </div>

      <div className={styles.metaCol2}>
        <Type.MetaHeading>Opening times</Type.MetaHeading>
        <ul className={styles.openingTimesList}>
          {openingTimes?.map(({ day, hours }) => (
            <li key={day}>
              {day}: {hours}
            </li>
          ))}
        </ul>

        {displayRating && <CqcRating cqcId={cqcId} />}
      </div>

      {showServices && (
        <div className={styles.services}>
          <Type.MetaHeading>Service(s)</Type.MetaHeading>
          <ServicesList nodes={services} />
        </div>
      )}

      {showDirections && directions?.length && (
        <div className={styles.directions} id={directionsId}>
          <Type.MetaHeading>Getting here</Type.MetaHeading>
          <CustomAccordion collapsible isBordered>
            {directions?.map(({ title, instructions }, index) => (
              <CustomAccordionItem uuid={index} key={index}>
                <CustomAccordionItemHeading>
                  <CustomAccordionItemButton>{title}</CustomAccordionItemButton>
                </CustomAccordionItemHeading>
                <CustomAccordionItemPanel>
                  <HtmlContent content={instructions} />
                </CustomAccordionItemPanel>
              </CustomAccordionItem>
            ))}
          </CustomAccordion>

          {googleDirections && (
            <Button to={googleDirections} size={sizes.LARGE}>
              Get directions
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export { LocationDetails as default };
